import { Dialog } from "@hellocarbo/lasagne";
import { use } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { PortalContainerContext, UserContext } from "@/contexts";
import useOutboundQueryParams from "@/hooks/useOutboundQueryParams";
import useSegmentEvent from "@/hooks/useSegmentEvent";

interface UpsellModalProps {
  children: React.ReactNode;
  isDisabled: boolean;
}

const UpsellModal = ({ children, isDisabled }: UpsellModalProps) => {
  const { user } = use(UserContext);
  const { container } = use(PortalContainerContext);
  const { t } = useTranslation("simulation");
  const navigate = useNavigate();
  const { sendSegmentEvent } = useSegmentEvent();
  const freetrial_params = useOutboundQueryParams();
  const subscription_params = useOutboundQueryParams();
  const isStartSubscriptionType = user?.company.subscriptionType === "start";
  const bookDemoUrl = `https://www.hellocarbo.com/reservez-votre-demo${freetrial_params}`;
  const coachCarboUrl = `https://try.hellocarbo.com/meetings/emmanuel-watrinet/rdv-abonnement-carbo-entreprise${subscription_params}`;

  const onTriggerClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    const target = e.target as HTMLElement;
    if (
      target.nodeName === "BUTTON" &&
      target.classList.contains("upsell-modal-trigger")
    ) {
      // If the click is on _this_ button, don't prevent default so the modal can open
      return;
    }
    e.preventDefault(); // Prevent the default behavior of the children (links / selects)
    target.parentElement?.closest("button")?.click();
    return;
  };

  if (user?.company.subscriptionType === "premium") {
    return children;
  }

  const onPrimaryClick = () => {
    sendSegmentEvent(
      isStartSubscriptionType ? "Subscription Asked" : "clic_CTA_upsell",
      {
        size: `${user?.company.employees}`,
      }
    );

    window.location.href = isStartSubscriptionType
      ? bookDemoUrl
      : coachCarboUrl;
  };
  const onSecondaryClick = () => {
    sendSegmentEvent("Discover the plans", {
      size: `${user?.company.employees}`,
    });
    navigate("/pricing");
  };

  return (
    <Dialog
      trigger={
        <button
          className="upsell-modal-trigger"
          onClick={onTriggerClick}
          disabled={isDisabled}
        >
          {children}
        </button>
      }
      title={t("modal.title")}
      description={t("modal.content")}
      warningMessage={t("modal.warning")}
      portalContainer={container as Element}
      primaryAction={{
        buttonText: isStartSubscriptionType
          ? t("modal.primaryCta")
          : t("modal.paidAccountCta"),
        onClick: onPrimaryClick,
      }}
      secondaryAction={
        isStartSubscriptionType
          ? {
              buttonText: t("modal.secondaryCta"),
              onClick: onSecondaryClick,
            }
          : undefined
      }
    />
  );
};

export default UpsellModal;
